// FileProgressForm.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  InputLabel,
  FormControl,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  useMediaQuery,
  FormHelperText,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../../components/Header";
import {
  generateAndExecuteMutation,
  graphqlQuery,
  fetchDataEngine,
} from "../../../../data/Axios/DynamicService";
import {
  caseManagementUrl,
  setupManagementUrl,
  userManagementUrl,
} from "../../../../config";
import { allUsersQuery, allCourts } from "../../../../data/Axios/queries";
import { format, parseISO } from "date-fns";

// Define steps for the Court Attendance stepper
const courtAttendanceSteps = [
  "Core Details",
  "Proceedings & Time",
  "Next Steps",
];

const FileProgressForm = (props) => {
  const { isEditing, data: editData, onClose, onAction } = props;

  // URLs and Session Data
  const base_url = caseManagementUrl.uri;
  const setup_url = setupManagementUrl.uri;
  const users_url = userManagementUrl.uri;
  const caseIdFromSession = JSON.parse(localStorage.getItem("CaseId"));
  const clientidFromSession = JSON.parse(localStorage.getItem("ClientId"));
  const fileNoFromSession = JSON.parse(localStorage.getItem("fileNumber"));
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userIdFromSession = parseInt(decodedToken.Id);
   const companyIdFromSession = parseInt(decodedToken.CompanyId);

  // State Management
  const [currentCase, setCurrentCase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [selectedType, setSelectedType] = useState("");

  useEffect(() => {
    if (isEditing && editData) {
      console.log("DEBUG editData on edit:", editData);

      if (editData.activityType) {
        // Normalize "Bring Up" to "File Bring Up" to match the form's expected values
        const type = editData.activityType === "Bring Up" ? "File Bring Up" : editData.activityType;
        setSelectedType(type);
      } else if (editData.courtId || editData.courtDate) {
        setSelectedType("Court Attendance");
      } else if (editData.nextStep || editData.nextBringUpDate) {
        // If there’s a next step, it’s a bring-up or progress + bring-up
        if (editData.actionTaken) {
          setSelectedType("File Progress & Bring Up");
        } else {
          setSelectedType("File Bring Up");
        }
      } else {
        // Fallback / Heuristic
        // If we have remarks but no actionTaken, it's likely a Bring Up
        if (editData.remarks && !editData.actionTaken) {
          setSelectedType("File Bring Up");
        } else {
          setSelectedType("File Progress");
        }
      }
    }
  }, [isEditing, editData]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0); // For stepper
  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Data Fetching
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const [usersData, courtsData, filesData] = await Promise.all([
          graphqlQuery(allUsersQuery, users_url),
          graphqlQuery(allCourts, setup_url),
        ]);

        if (usersData) setUsers(usersData);
        if (courtsData) setCourts(courtsData);
      } catch (error) {
        console.error("Error fetching initial form data:", error);
        Swal.fire(
          "Error",
          "Could not load initial data. Please try again.",
          "error"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // Stepper Navigation
  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Dynamic Validation Schema
  const getValidationSchema = () => {
    if (selectedType === "Court Attendance") {
      switch (activeStep) {
        case 0: // Core Details
          return yup.object({
            courtId: yup.string().required("Court is required"),
            courtDate: yup.date().required("Date of Attendance is required"),
            personDealingId: yup
              .number()
              .nullable()
              .required("Person Dealing is required"),
          });
        case 1: // Proceedings
          return yup.object({
            purposeOfAttendance: yup.string().required("Purpose is required"),
            outcome: yup.string().required("Outcome is required"),
          });

        default:
          return yup.object({});
      }
    }

    // Validation for Book Court Date
    if (selectedType === "Book Court Date") {
      return yup.object({
        courtEventTypeId: yup
          .number()
          .typeError("Event type is required")
          .required("Event type is required"),
        courtId: yup
          .number()
          .typeError("Court is required")
          .required("Court is required"),
        startDate: yup
          .date()
          .typeError("Start date is required")
          .required("Start date is required"),
        endDate: yup
          .date()
          .typeError("End date is required")
          .required("End date is required"),
        userAssigned: yup
          .number()
          .typeError("Advocate in charge is required")
          .required("Advocate in charge is required"),
      });
    }

    switch (selectedType) {
      case "File Progress & Bring Up":
        return yup.object({
          actionDate: yup.date().required("Action Date is required"),
          actionTaken: yup.string().required("Action Description is required"),
          nextStep: yup.string().required("Next Action is required"),
          nextBringUpDate: yup
            .date()
            .required("Next Bring Up Date is required"),
          reminderDays: yup
            .number()
            .min(1)
            .required("Reminder Period is required"),
        });
      case "File Progress":
        return yup.object({
          actionDate: yup.date().required("Action Date is required"),
          actionTaken: yup.string().required("Action Description is required"),
        });
      case "File Bring Up":
        return yup.object({
          actionDate: yup.date().required("Date is required"),
          remarks: yup.string().required("Reason is required"),
          nextBringUpDate: yup
            .date()
            .required("Next Bring Up Date is required"),
          reminderDays: yup
            .number()
            .min(1)
            .required("Reminder Period is required"),
        });
      default:
        return yup.object({
          type: yup.string().required("Please select a type"),
        });
    }
  };

  // Helper to parse time string like "1h 30m" into hours and minutes
  const parseTimeTaken = (timeString) => {
    if (!timeString || typeof timeString !== "string") {
      return { hours: 0, minutes: 0 };
    }
    const hoursMatch = timeString.match(/(\d+)h/);
    const minutesMatch = timeString.match(/(\d+)m/);
    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
    return { hours, minutes };
  };

  const { hours: initialHours, minutes: initialMinutes } = parseTimeTaken(
    editData?.timeTaken
  );
  console.log("Raw editData:", editData);
  console.log("Raw editData.personDealingId:", editData?.personDealingId);
  // Form Initial Values
  const initialValues = {
    type: selectedType || "",
    clientId: editData?.clientId || (currentCase ? currentCase.clientId : ""),
    fileId: editData?.fileId || (currentCase ? currentCase.id : ""),
    actionDate: editData?.actionDate
      ? format(parseISO(editData.actionDate), "yyyy-MM-dd")
      : "",
    actionType: editData?.actionType || 1, // 1 for External, 2 for Internal
    actionTaken: editData?.actionTaken || "",
    timeTakenHours: initialHours,
    timeTakenMinutes: initialMinutes,
    nextStep: editData?.nextStep || "",
    nextBringUpDate: editData?.nextBringUpDate
      ? format(parseISO(editData.nextBringUpDate), "yyyy-MM-dd")
      : "",
    reminderDays: editData?.reminderDays || 1,
    remarks: editData?.remarks || "",
    courtId: editData?.courtId || "",
    courtName: editData?.courtName || "",
    courtDate: editData?.courtDate
      ? format(parseISO(editData.courtDate), "yyyy-MM-dd")
      : "",
    judgeName: editData?.judgeName || "",
    purposeOfAttendance: editData?.purposeOfAttendance || "",
    outcome: editData?.outcome || "",
    nextOption: parseInt(editData?.nextOption || 0),
    nextOptionDate: editData?.nextOptionDate
      ? format(parseISO(editData.nextOptionDate), "yyyy-MM-dd")
      : "",
    nextAction: editData?.nextAction || "",
    nextAppearanceOption: editData?.nextAppearanceOption || "Hearing",
    otherFiles: editData?.otherFiles || [],
    caseNo: editData?.caseNumber || (currentCase ? currentCase.caseNumber : ""),
    parties: editData?.parties || "",
    personDealingId: editData?.personDealingId
      ? parseInt(editData.personDealingId, 10)
      : editData?.advocatePresent
        ? parseInt(editData.advocatePresent, 10)
        : null,

    plaintiff: editData?.plaintiff || "",
    defendant: editData?.defendant || "",
    petitioner: editData?.petitioner || "",
  };
  console.log("Edit Data:", editData);
  console.log("Current Data:", currentCase);
  console.log("Initial personDealingId:", initialValues.personDealingId);
  // Dropdown Options
  const fileOptions = allFiles.map((f) => ({
    value: f.id,
    label: `${f.caseNumber} - ${f.title}`,
  }));
  const courtOptions = courts.map((c) => ({ value: c.id, label: c.court }));
  const userOptions = users.map((u) => ({
    value: u.id,
    label: `${u.firstName} ${u.lastName}`,
  }));
  const formatSqlDate = (date) => {
    if (!date) return null;
    return new Date(date).toISOString(); // "2025-11-18T00:00:00.000Z"
  };

  const formatSqlDateTime = (date) => {
    if (!date) return null;
    return new Date(date).toISOString(); // "2025-11-21T03:00:00.000Z"
  };

  // Form Submission Handler
  const handleSubmit = async (values) => {
    if (
      selectedType === "Court Attendance" &&
      activeStep < courtAttendanceSteps.length - 1
    ) {
      handleNext();
      return;
    }

    setIsSubmitting(true);
    try {
      let mutationName = "";
      let mutationInputName = "";
      const timeTakenString = `${parseInt(values.timeTakenHours) || 0}h ${parseInt(values.timeTakenMinutes) || 0
        }m`;

      let mutationData = !isEditing
        ? {
          companyId: companyIdFromSession,
          clientId: clientidFromSession,
          fileNo: fileNoFromSession,
          fileId: caseIdFromSession,
          createdBy: userIdFromSession,

          isBillable: Number(values.isBillable),
          startTimerNow: Number(values.startTimerNow),
        }
        : {
          id: isEditing ? editData.id : 0,
          companyId: companyIdFromSession,
          clientId: clientidFromSession,
          fileNo: fileNoFromSession,
          fileId: caseIdFromSession,
          updatedBy: userIdFromSession,
        };

      switch (selectedType) {
        case "File Progress":
          mutationName = isEditing
            ? "updateFileProgress"
            : "createFileProgress";
          mutationInputName = "progress";
          mutationData = {
            ...mutationData,
            ...(!isEditing && { assigneeId: values.asigneeId || null }),
            actionDate: values.actionDate,
            actionType: values.actionType,
            actionTaken: values.actionTaken,
            timeTaken: timeTakenString,
            remarks: values.remarks || "", // Added remarks field
          };
          break;

        case "File Progress & Bring Up":
          // First create file progress
          const progressMutationData = {
            ...mutationData,
            ...(!isEditing && { assigneeId: values.asigneeId || null }),
            actionDate: formatSqlDate(values.actionDate),
            actionType: values.actionType,
            actionTaken: values.actionTaken,
            timeTaken: timeTakenString,
            remarks: values.remarks || "",
          };

          const progressResponse = await generateAndExecuteMutation(
            isEditing ? "updateFileProgress" : "createFileProgress",
            progressMutationData,
            "progress",
            base_url
          );

          if (progressResponse) {
            // Then create file bring up
            const bringUpMutationData = {
              ...mutationData,
              actionDate: values.actionDate ? new Date(values.actionDate).toISOString() : null,
              nextStep: values.nextStep,
              nextBringUpDate: formatSqlDateTime(values.nextBringUpDate),
              reminderDays: parseInt(values.reminderDays),
              remarks: values.remarks || "",
            };

            await generateAndExecuteMutation(
              isEditing ? "updateFileBringUp" : "createFileBringUp",
              bringUpMutationData,
              "bringUp",
              base_url
            );
          }
          break;

        case "File Bring Up":
          mutationName = isEditing ? "updateFileBringUp" : "createFileBringUp";
          mutationInputName = "bringUp";
          mutationData = {
            ...mutationData,
            ...(!isEditing && { assigneeId: values.asigneeId || null }),
            actionDate: values.actionDate,
            remarks: values.remarks,
            nextStep: values.nextStep,
            nextBringUpDate: values.nextBringUpDate,
            reminderDays: parseInt(values.reminderDays),
            actionTaken: values.actionTaken || "", // Added actionTaken field
          };
          break;

        // Inside handleSubmit's switch statement
        case "Book Court Date":
          // Determine the correct mutation name and input name for booking
          mutationName = isEditing ? "updateBookedCourtDate" : "bookCourtAttendance"; // Replace with actual update mutation if it exists
          mutationInputName = "courtAttendance"; // Adjust based on your schema
          mutationData = {
            ...mutationData, // Include common data if applicable
            // Map your form values to the fields expected by the bookCourtAttendance mutation
            courtEventTypeId: parseInt(values.courtEventTypeId), // Example mapping
            courtId: parseInt(values.courtId), // Example mapping
             dateOfAttendance: values.startDate, // Example mapping (check format needed by backend)
            // endDate: values.endDate,     // Example mapping
            // startTime: values.startTime, // Example mapping (check format needed)
            // endTime: values.endTime,     // Example mapping
            advocateInCharge: parseInt(values.userAssigned), // Example mapping (check backend field name)
            assistantAdvocate: values.assistedBy ? parseInt(values.assistedBy) : null, // Example mapping
            remarks: values.remarks || "", // Example mapping
            // Add other fields from your form as required by the mutation
          };
          break; // Crucial: Don't forget the break statement

        case "Court Attendance":
          mutationName = isEditing
            ? "updateCourtAttendance"
            : "createCourtAttendance";
          mutationInputName = "attendance";

          const nextOptionValue = values.nextOption === "Bring Up" ? 1 : 2;

          // // ✅ Combine time into a single string (e.g., "2h 30m")
          // const timeTakenString = `${parseInt(values.timeTakenHours) || 0}h ${
          //   parseInt(values.timeTakenMinutes) || 0
          // }m`;

          mutationData = {
            ...mutationData,
            courtId: parseInt(values.courtId),
            courtDate: values.courtDate,
            judgeName: values.judgeName,
            purposeOfAttendance: values.purposeOfAttendance,
            outcome: values.outcome,
            // timeTaken: timeTakenString, // ✅ added field
            // timeTakenHours: parseInt(values.timeTakenHours) || 0, // ✅ optional
            // timeTakenMinutes: parseInt(values.timeTakenMinutes) || 0, // ✅ optional
            nextOption: nextOptionValue,
            nextOptionDate: values.nextOptionDate,
            nextAction: values.nextAction,
            nextActionReminderPeriod: parseInt(values.reminderDays) || 1,
            caseNumber: values.caseNo,
            petitioner: values.plaintiff || values.petitioner,
            defendant: values.defendant,
            appearanceType: values.nextAppearanceOption,
            advocatePresent: values.personDealingId || "",
          };
          break;

        default:
          throw new Error("Invalid type selected");
      }

      // For single mutation operations (not File Progress & Bring Up which handles its own mutations)
      if (selectedType !== "File Progress & Bring Up") {
        const response = await generateAndExecuteMutation(
          mutationName,
          mutationData,
          mutationInputName,
          base_url
        );

        if (response) {
          onClose();
          if (onAction) onAction();
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Record ${isEditing ? "updated" : "created"} successfully.`,
          });
        }
      } else {
        // For File Progress & Bring Up, success is handled in the mutation chain
        onClose();
        if (onAction) onAction();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Record ${isEditing ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred. Please try again.",
      });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormFields = (formikProps) => {
    const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
      formikProps;

    const commonFields = (
      <>
        {/* <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Client"
            value={
              currentCase
                ? currentCase.clientType === 1
                  ? `${currentCase.individualFirstName} ${currentCase.individualLastName}`
                  : currentCase.corporateCompanyName
                : "Loading..."
            }
            variant="filled"
            InputProps={{ readOnly: true }}
          />
        </Grid> */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="File / Case"
            value={fileNoFromSession || "N/A"}
            variant="filled"
            InputProps={{ readOnly: true }}
          />
        </Grid>
      </>
    );
    const BillableFields = (
      <>
        <Grid item xs={12} sm={6}>
          {!props.isEditing && (
            <>
              {/* Billable and Timer Options */}
              <FormControlLabel
                control={
                  <Checkbox
                    name="isBillable"
                    checked={values.isBillable}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("isBillable", checked);

                      // If unchecked, also stop timer and clear assignee
                      if (!checked) {
                        setFieldValue("startTimerNow", false);
                        setFieldValue("asigneeId", "");
                      }
                    }}
                  />
                }
                label="Billable"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="startTimerNow"
                    checked={values.startTimerNow}
                    onChange={(e) =>
                      setFieldValue("startTimerNow", e.target.checked)
                    }
                    disabled={!values.isBillable}
                  />
                }
                label="Start Timer Now"
              />

              {/* Show Assignee Dropdown only when Billable is checked */}
              {values.isBillable &&
                (selectedType === "File Progress" ||
                  selectedType === "File Progress & Bring Up" ||
                  selectedType === "File Bring Up") && (
                  <FormControl
                    fullWidth
                    variant="filled"
                    error={touched.asigneeId && !!errors.asigneeId}
                    sx={{ mt: 2 }}
                  >
                    <InputLabel id="asignee-label">Assigned To</InputLabel>
                    <Select
                      labelId="asignee-label"
                      value={values.asigneeId}
                      label="Assigned To"
                      name="asigneeId"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Assignee
                      </MenuItem>
                      {userOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.asigneeId && errors.asigneeId && (
                      <FormHelperText>{errors.asigneeId}</FormHelperText>
                    )}
                  </FormControl>
                )}
            </>
          )}
        </Grid>
      </>
    );

    const timeTrackingFields = (
      <>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Time Taken (Hours)"
            name="timeTakenHours"
            value={values.timeTakenHours}
            onChange={handleChange}
            onBlur={handleBlur}
            variant="filled"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Time Taken (Minutes)"
            name="timeTakenMinutes"
            value={values.timeTakenMinutes}
            onChange={handleChange}
            onBlur={handleBlur}
            variant="filled"
          />
        </Grid>
      </>
    );

    switch (selectedType) {
      case "File Progress":
      case "File Progress & Bring Up":
        return (
          <Grid container spacing={2} mt={1}>
            {commonFields}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Progress"
                name="actionDate"
                value={values.actionDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.actionDate && !!errors.actionDate}
                helperText={touched.actionDate && errors.actionDate}
                InputLabelProps={{ shrink: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="filled">
                <InputLabel>Action Type</InputLabel>
                <Select
                  name="actionType"
                  value={values.actionType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value={1}>
                    External Action (Appears in progress reports)
                  </MenuItem>
                  <MenuItem value={2}>Internal Action</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Action Description"
                name="actionTaken"
                value={values.actionTaken}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.actionTaken && !!errors.actionTaken}
                helperText={touched.actionTaken && errors.actionTaken}
                variant="filled"
              />
            </Grid>
            {timeTrackingFields}
            {selectedType === "File Progress & Bring Up" && (
              <>
                <Grid item xs={12}>
                  <hr style={{ margin: "10px 0" }} />
                  <Typography variant="h6">Bring Up Details</Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Next Action (Way Forward)"
                    name="nextStep"
                    value={values.nextStep}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.nextStep && !!errors.nextStep}
                    helperText={touched.nextStep && errors.nextStep}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Next Bring Up Date"
                    name="nextBringUpDate"
                    value={values.nextBringUpDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.nextBringUpDate && !!errors.nextBringUpDate}
                    helperText={
                      touched.nextBringUpDate && errors.nextBringUpDate
                    }
                    InputLabelProps={{ shrink: true }}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Reminder Period (Days)"
                    name="reminderDays"
                    value={values.reminderDays}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.reminderDays && !!errors.reminderDays}
                    helperText={touched.reminderDays && errors.reminderDays}
                    variant="filled"
                  />
                </Grid>
              </>
            )}
            {BillableFields}
          </Grid>
        );

      case "File Bring Up":
        return (
          <Grid container spacing={2} mt={1}>
            {commonFields}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                name="actionDate"
                value={values.actionDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.actionDate && !!errors.actionDate}
                helperText={touched.actionDate && errors.actionDate}
                InputLabelProps={{ shrink: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason / Remarks"
                name="remarks"
                value={values.remarks}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.remarks && !!errors.remarks}
                helperText={touched.remarks && errors.remarks}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Next Bring Up Date"
                name="nextBringUpDate"
                value={values.nextBringUpDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.nextBringUpDate && !!errors.nextBringUpDate}
                helperText={touched.nextBringUpDate && errors.nextBringUpDate}
                InputLabelProps={{ shrink: true }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Reminder Period (Days)"
                name="reminderDays"
                value={values.reminderDays}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.reminderDays && !!errors.reminderDays}
                helperText={touched.reminderDays && errors.reminderDays}
                variant="filled"
              />
            </Grid>
            {BillableFields}
          </Grid>
        );

      case "Court Attendance":
        const nextDateLabel =
          values.nextOption === "Bring Up"
            ? "Bring-Up Date"
            : `Next ${values.nextAppearanceOption || ""} Date`.trim();
        return (
          <Box>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{ mt: 3, mb: 3 }}
            >
              {courtAttendanceSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {activeStep === 0 && (
              <Grid container spacing={2}>
                {commonFields}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={fileOptions}
                    getOptionLabel={(option) => option.label}
                    value={fileOptions.filter((o) =>
                      values.otherFiles.includes(o.value)
                    )}
                    onChange={(e, value) =>
                      setFieldValue(
                        "otherFiles",
                        value.map((v) => v.value)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label="Other Files (Optional)"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={courtOptions}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      courtOptions.find((o) => o.value === values.courtId) ||
                      null
                    }
                    onChange={(e, value) =>
                      setFieldValue("courtId", value ? value.value : "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Court"
                        variant="filled"
                        error={touched.courtId && !!errors.courtId}
                        helperText={touched.courtId && errors.courtId}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Case No."
                    name="caseNo"
                    value={values.caseNo}
                    onChange={handleChange}
                    variant="filled"
                    InputProps={{ readOnly: false }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Parties"
                    name="parties"
                    value={values.parties}
                    onChange={handleChange}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Judge/Magistrate"
                    name="judgeName"
                    value={values.judgeName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={userOptions}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      userOptions.find(
                        (o) =>
                          String(o.value) === String(values.personDealingId)
                      ) || null
                    }
                    onChange={(e, value) =>
                      setFieldValue(
                        "personDealingId",
                        value ? value.value : null
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Person Dealing"
                        variant="filled"
                        error={
                          touched.personDealingId && !!errors.personDealingId
                        }
                        helperText={
                          touched.personDealingId && errors.personDealingId
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Attendance"
                    name="courtDate"
                    value={values.courtDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.courtDate && !!errors.courtDate}
                    helperText={touched.courtDate && errors.courtDate}
                    InputLabelProps={{ shrink: true }}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Plaintiff/Petitioner"
                    name="plaintiff"
                    value={values.plaintiff || values.petitioner}
                    onChange={handleChange}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Defendant/Respondent"
                    name="defendant"
                    value={values.defendant}
                    onChange={handleChange}
                    variant="filled"
                  />
                </Grid>
              </Grid>
            )}
            {activeStep === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Purpose of Attendance"
                    name="purposeOfAttendance"
                    value={values.purposeOfAttendance}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.purposeOfAttendance &&
                      !!errors.purposeOfAttendance
                    }
                    helperText={
                      touched.purposeOfAttendance && errors.purposeOfAttendance
                    }
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Proceedings/Progress & Outcome"
                    name="outcome"
                    value={values.outcome}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.outcome && !!errors.outcome}
                    helperText={touched.outcome && errors.outcome}
                    variant="filled"
                  />
                </Grid>
                {timeTrackingFields}
              </Grid>
            )}
            {activeStep === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Next Option</InputLabel>
                    <Select
                      name="nextOption"
                      value={values.nextOption}
                      onChange={handleChange}
                    >
                      <MenuItem value="Bring Up">Bring Up</MenuItem>
                      <MenuItem value="Court Attendance">
                        Court Attendance
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {values.nextOption === "Court Attendance" && (
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Next Appearance Option</InputLabel>
                      <Select
                        name="nextAppearanceOption"
                        value={values.nextAppearanceOption || ""}
                        onChange={handleChange}
                      >
                        <MenuItem value="Hearing">Hearing</MenuItem>
                        <MenuItem value="Mentions">Mentions</MenuItem>
                        <MenuItem value="Ruling">Ruling</MenuItem>
                        <MenuItem value="Judgement">Judgement</MenuItem>
                        <MenuItem value="Taxation">Taxation</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label={nextDateLabel}
                    name="nextOptionDate"
                    value={values.nextOptionDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Reminder Period (Days)"
                    name="reminderDays"
                    value={values.reminderDays}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="filled"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Next Action"
                    name="nextAction"
                    value={values.nextAction}
                    onChange={handleChange}
                    variant="filled"
                  />
                </Grid>
                {BillableFields}
              </Grid>
            )}
          </Box>
        );

      // ====== NEW: Book Court Date UI (simplified single page) ======
      case "Book Court Date":
        return (
          <Grid container spacing={2} mt={1}>
            {commonFields}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="filled">
                <InputLabel>Court Event Type</InputLabel>
                <Select
                  name="courtEventTypeId"
                  value={values.courtEventTypeId || ""}
                  onChange={handleChange}
                >
                  <MenuItem value={1}>Hearing</MenuItem>
                  <MenuItem value={2}>Mention</MenuItem>
                  <MenuItem value={3}>Ruling</MenuItem>
                  <MenuItem value={4}>Judgment</MenuItem>
                  <MenuItem value={5}>Taxation</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={courtOptions}
                getOptionLabel={(option) => option.label || ""}
                value={
                  courtOptions.find((o) => o.value === values.courtId) || null
                }
                onChange={(e, value) =>
                  setFieldValue("courtId", value ? value.value : "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Court"
                    variant="filled"
                    error={touched.courtId && !!errors.courtId}
                    helperText={touched.courtId && errors.courtId}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                name="startDate"
                value={values.startDate || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="filled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Start Time"
                name="startTime"
                value={values.startTime || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="filled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="endDate"
                value={values.endDate || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="filled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="End Time"
                name="endTime"
                value={values.endTime || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="filled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={userOptions}
                getOptionLabel={(option) => option.label || ""}
                value={
                  userOptions.find((o) => o.value === values.userAssigned) ||
                  null
                }
                onChange={(e, value) =>
                  setFieldValue("userAssigned", value ? value.value : "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Advocate In Charge"
                    variant="filled"
                    error={touched.userAssigned && !!errors.userAssigned}
                    helperText={touched.userAssigned && errors.userAssigned}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={userOptions}
                getOptionLabel={(option) => option.label || ""}
                value={
                  userOptions.find((o) => o.value === values.assistedBy) || null
                }
                onChange={(e, value) =>
                  setFieldValue("assistedBy", value ? value.value : "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assistant Advocate"
                    variant="filled"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks / Notes"
                name="remarks"
                value={values.remarks || ""}
                onChange={handleChange}
                variant="filled"
              />
            </Grid>
            {BillableFields}
          </Grid>
        );

      default:
        return <Typography mt={2}>Please select a type to begin.</Typography>;
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: "40vh" }}
      >
        <CircularProgress color="secondary" />
        <Typography ml={2}>Loading Case Details...</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit File Activity" : "Create File Activity"}
        subtitle="Record File Progress, Bring Ups, or Court Attendance"
      />
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          ...initialValues, // your existing values if any
          isBillable: false,
          startTimerNow: false,
        }}
        validationSchema={getValidationSchema()}
        enableReinitialize
      >
        {(formikProps) => (
          <form onSubmit={formikProps.handleSubmit}>
            {!isEditing && (
              <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
                <InputLabel>Select Type</InputLabel>
                <Select
                  name="type"
                  value={selectedType}
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    formikProps.setFieldValue("type", e.target.value);
                    setActiveStep(0);
                  }}
                >
                  <MenuItem value="File Progress & Bring Up">
                    Add File Progress & Bring Up
                  </MenuItem>
                  <MenuItem value="File Progress">
                    Add File Progress ONLY
                  </MenuItem>
                  <MenuItem value="File Bring Up">
                    Add File Bring Up ONLY
                  </MenuItem>
                  <MenuItem value="Court Attendance">
                    Add Court Attendance
                  </MenuItem>
                  <MenuItem value="Book Court Date">Book Court Date</MenuItem>
                </Select>
              </FormControl>
            )}

            {selectedType && (
              <Header
                subtitle={`Enter details for: ${selectedType}`}
                sx={{ mt: 2, mb: -1 }}
              />
            )}
            {renderFormFields(formikProps)}
            <Box display="flex" justifyContent="space-between" mt="30px">
              {selectedType === "Court Attendance" && activeStep > 0 ? (
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={handleBack}
                >
                  Back
                </Button>
              ) : (
                <Box />
              )}

              {selectedType && (
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : selectedType === "Court Attendance" &&
                      activeStep < courtAttendanceSteps.length - 1
                      ? "Next"
                      : isEditing
                        ? "Update Record"
                        : "Add Record"}
                </Button>
              )}
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default FileProgressForm;
